import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Alert,
  Paper,
  Stack,
  alpha,
  Button,
  Collapse,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { BoolNode } from 'src/services/common.types';
import { useTransformSearchDSLMutation } from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';

import { SearchFormValues } from '../types/search.types';
import AdvancedSearchBuilder from './advanced-search-builder';
import { createExampleDSL, combineKeywordAndBuilder } from '../utils/search.utils';

interface CandidateSearchFiltersProps {
  onSearch: (values: SearchFormValues) => void;
  onClearFilters: () => void;
  initialValues?: SearchFormValues;
}

export default function CandidateSearchFilters({
  onSearch,
  onClearFilters,
  initialValues = {},
}: CandidateSearchFiltersProps) {

  const { t } = useTranslate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [naturalLanguageText, setNaturalLanguageText] = useState('');
  const [showNLSearch, setShowNLSearch] = useState(false);
  const [transformError, setTransformError] = useState<string | null>(null);

  const [transformSearchDSL, { isLoading: isTransforming }] = useTransformSearchDSLMutation();

  const { control, handleSubmit, reset, setValue } = useForm<SearchFormValues>({
    defaultValues: {
      ...initialValues,
      search_dsl: initialValues.search_dsl || { op: 'AND', children: [] }
    },
  });

  // reset form when initialValues change
  useEffect(() => {
    reset({
      ...initialValues,
      search_dsl: initialValues.search_dsl || { op: 'AND', children: [] }
    });
  }, [initialValues, reset]);

  const onSubmit = async (data: SearchFormValues) => {
    setIsSubmitting(true);
    try {
      // Fold the top-level keyword field into the builder DSL as one combined query (item 23); the raw
      // keyword is also surfaced so an opened record can highlight it (item 18).
      onSearch({ search_dsl: combineKeywordAndBuilder(keyword, data.search_dsl), keyword });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    reset({
      search_dsl: { op: 'AND', children: [] }
    });
    setKeyword('');
    onClearFilters();
  };

  const handleAdvancedSearchDSLChange = (dsl: BoolNode) => {
    setValue('search_dsl', dsl);
  };

  const handleLoadExample = () => {
    const exampleDSL = createExampleDSL();
    setValue('search_dsl', exampleDSL);
  };

  const handleNaturalLanguageSearch = async () => {
    if (!naturalLanguageText.trim()) {
      setTransformError('Please enter a search query');
      return;
    }

    setTransformError(null);

    try {
      const result = await transformSearchDSL({ text: naturalLanguageText }).unwrap();
      setValue('search_dsl', result);
      setNaturalLanguageText('');
      setShowNLSearch(false);
      onSearch({ search_dsl: combineKeywordAndBuilder(keyword, result), keyword });
    } catch (error: any) {
      setTransformError(error?.data?.message || 'Failed to transform search query. Please try again.');
    }
  };

  const handleToggleNLSearch = () => {
    setShowNLSearch(!showNLSearch);
    setTransformError(null);
    if (showNLSearch) {
      setNaturalLanguageText('');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>

        {/* Keyword / Boolean search (item 23) — first, full-width; AND-combines with the builder below. */}
        <TextField
          fullWidth
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={t('candidates.search.keyword_placeholder', { defaultValue: 'Search by keyword' })}
          InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        />

        {/* Natural language search section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                theme.palette.primary.main,
                0.02
              )} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2,
          }}
        >
          <Stack spacing={0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify
                  icon="solar:magic-stick-3-bold-duotone"
                  width={24}
                  sx={{ color: 'primary.main' }}
                />
                <Stack spacing={0}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('components.advanced_search.ai_search_title')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('components.advanced_search.ai_search_description')}
                  </Typography>
                </Stack>
              </Box>
              <Button
                size="small"
                onClick={handleToggleNLSearch}
                startIcon={
                  <Iconify icon={showNLSearch ? 'eva:minus-outline' : 'eva:plus-outline'} />
                }
              >
                {showNLSearch ? 'Hide' : 'Show'}
              </Button>
            </Box>

            <Collapse in={showNLSearch}>
              <Stack spacing={2} mt={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={naturalLanguageText}
                  onChange={(e) => setNaturalLanguageText(e.target.value)}
                  placeholder={t('components.advanced_search.ai_search_placeholder')}
                  variant="outlined"
                  disabled={isTransforming}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      handleNaturalLanguageSearch();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />

                {transformError && (
                  <Alert severity="error" onClose={() => setTransformError(null)}>
                    {transformError}
                  </Alert>
                )}

                <LoadingButton
                  fullWidth
                  variant="contained"
                  loading={isTransforming}
                  onClick={handleNaturalLanguageSearch}
                  startIcon={<Iconify icon="solar:magic-stick-3-bold" />}
                  sx={{
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    },
                  }}
                >
                  {t('components.advanced_search.transform_text')}
                </LoadingButton>

                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center' }}
                >
                  {t('components.advanced_search.ai_search_hint')}
                </Typography>
              </Stack>
            </Collapse>
          </Stack>
        </Paper>

        {/* advanced search builder */}
        <Controller
          name="search_dsl"
          control={control}
          render={({ field: { value } }) => (
            <Box>
              <AdvancedSearchBuilder
                value={value || { op: 'AND', children: [] }}
                onChange={handleAdvancedSearchDSLChange}
              />
              <Button
                size="small"
                startIcon={<Iconify icon="eva:bulb-outline" />}
                onClick={handleLoadExample}
                sx={{ mt: 2 }}
              >
                {t('candidates.search.load_example')}
              </Button>
            </Box>
          )}
        />

        <Stack direction="row" spacing={1.5}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<Iconify icon="eva:search-fill" />}
          >
            {t('common.search')}
          </LoadingButton>

          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<Iconify icon="eva:trash-2-outline" />}
          >
            {t('common.clear')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}